import type { CatalogSector } from './workfieldCatalog';
import type { Enum } from '~~/utils/enum';

export const PROFILE_REVIEW_BAND = {
    CRITICAL: 'critical',
    TO_REVIEW: 'to-review',
    LOW_FIT: 'low-fit',
    ALIGNED: 'aligned',
    ACKNOWLEDGED: 'acknowledged',
} as const;

export type ProfileReviewBand = Enum<typeof PROFILE_REVIEW_BAND>;

export const PROFILE_REVIEW_DECISION = {
    REMOVE: 'remove',
    KEEP: 'keep',
    ADD: 'add',
    DECLINE_ADD: 'decline_add',
    CHANGE_PRIMARY: 'change_primary',
    KEEP_PRIMARY: 'keep_primary',
    HIGHLIGHT: 'highlight',
    UNHIGHLIGHT: 'unhighlight',
    CONFIRM_LANGUAGES: 'confirm_languages',
} as const;

export type ProfileReviewDecisionType = Enum<typeof PROFILE_REVIEW_DECISION>;

export const PROFILE_REVIEW_SOURCE = {
    PROFILE_REVIEW: 'profile-review',
    VISIBILITY: 'visibility',
    DASHBOARD: 'dashboard',
    MESSAGES: 'messages',
    DECLINE_MODAL: 'decline-modal',
    EMAIL: 'email',
} as const;

export type ProfileReviewSource = Enum<typeof PROFILE_REVIEW_SOURCE>;

export const REVIEW_LANGUAGES = ['fr', 'nl', 'en', 'de'] as const;

export const MAX_HIGHLIGHTS = 3;

export const PROFILE_REVIEW_FLOW_MODAL = 'profile-review-flow';

export const PROFILE_REVIEW_FAVOURITE_MODAL = 'profile-review-favourite-swap';

export type ProfileReviewRemoval = { slug: string; band: 'low' | 'medium'; percent: number };
export type ProfileReviewAddition = { slug: string; band: 'strong' | 'medium'; percent: number; because: string[] };

export type ProfileReview = {
    professionalId: string;
    isPremium: boolean;
    band: ProfileReviewBand | null;
    languages: { items: string[]; confirmedAt: string | null };
    reviewedAt: string | null;
    removals: ProfileReviewRemoval[];
    additions: ProfileReviewAddition[];
    primary: { current: string | null; suggested: string | null; gap: number | null };
    highlights: string[];
};

export type ProfileReviewDecision = {
    type: ProfileReviewDecisionType;
    source: ProfileReviewSource;
    slug?: string;
    languages?: string[];
};

// `invalid`: the API refused the answer itself (422); any other error is the network, a timeout or the server.
export type ProfileReviewResult = { status: 'ok'; review: ProfileReview } | { status: 'forbidden' } | { status: 'error'; reason?: 'invalid' };

export type ProfileReviewFailureReason = 'network' | 'invalid' | 'forbidden';

export interface ProfileReviewGateway {
    get(professionalId: string): Promise<ProfileReviewResult>;
    decide(professionalId: string, decision: ProfileReviewDecision): Promise<ProfileReviewResult>;
}

const BANNER_BANDS: ProfileReviewBand[] = [PROFILE_REVIEW_BAND.TO_REVIEW, PROFILE_REVIEW_BAND.LOW_FIT];

export const isBannerBand = (band: ProfileReviewBand | null): boolean => band !== null && BANNER_BANDS.includes(band);

const SETTLED_BANDS: ProfileReviewBand[] = [PROFILE_REVIEW_BAND.ALIGNED, PROFILE_REVIEW_BAND.ACKNOWLEDGED];

/** A settled profile has nothing left to review, except languages never confirmed: its banner then opens that card alone. */
export const hasBanner = (review: ProfileReview): boolean => isBannerBand(review.band) || (review.band !== null && SETTLED_BANDS.includes(review.band) && review.languages.confirmedAt === null);

// The sidebar dot starts at low-fit, so it also covers critical, which has no banner: that profile goes to the care team.
const DOT_BANDS: ProfileReviewBand[] = [PROFILE_REVIEW_BAND.CRITICAL, ...BANNER_BANDS];

export const isDotBand = (band: ProfileReviewBand | null): boolean => band !== null && DOT_BANDS.includes(band);

export const needsPrimaryChange = (review: ProfileReview): boolean => review.primary.suggested !== null && review.primary.suggested !== review.primary.current;

export type FlowCardType = 'languages' | 'intro-remove' | 'remove' | 'intro-add' | 'add' | 'primary';

export type FlowCard = { key: string; type: FlowCardType; slug?: string };

export const isDecisionCard = (card: FlowCard): boolean => !card.type.startsWith('intro');

/** Cards known when the flow opens. The primary card is not here: its suggestion depends on the answers given before it. */
export function buildFlowCards(review: ProfileReview): FlowCard[] {
    const cards: FlowCard[] = [];
    if (review.languages.confirmedAt === null) cards.push({ key: 'languages', type: 'languages' });
    if (review.removals.length > 0) {
        cards.push({ key: 'intro-remove', type: 'intro-remove' });
        review.removals.forEach((removal) => cards.push({ key: `remove:${removal.slug}`, type: 'remove', slug: removal.slug }));
    }
    if (review.additions.length > 0) {
        cards.push({ key: 'intro-add', type: 'intro-add' });
        review.additions.forEach((addition) => cards.push({ key: `add:${addition.slug}`, type: 'add', slug: addition.slug }));
    }
    return cards;
}

export const PRIMARY_CARD: FlowCard = { key: 'primary', type: 'primary' };

export const PENDING_DECISION_BY_CARD: Record<'remove' | 'add' | 'primary' | 'languages', ProfileReviewDecisionType[]> = {
    remove: [PROFILE_REVIEW_DECISION.REMOVE, PROFILE_REVIEW_DECISION.KEEP],
    add: [PROFILE_REVIEW_DECISION.ADD, PROFILE_REVIEW_DECISION.DECLINE_ADD],
    primary: [PROFILE_REVIEW_DECISION.CHANGE_PRIMARY, PROFILE_REVIEW_DECISION.KEEP_PRIMARY],
    languages: [PROFILE_REVIEW_DECISION.CONFIRM_LANGUAGES],
};

export type ProfileChip = { slug: string; name: string; favourite: boolean; pendingRemoval: boolean; sharedWith: string[] };
export type ProfileSectorView = { slug: string; name: string; chips: ProfileChip[] };
export type ProfileView = { main: ProfileSectorView | null; others: ProfileSectorView[] };

type ProfileViewInput = {
    workfieldSlugs: string[];
    primarySlug: string | null;
    catalog: CatalogSector[];
    highlights: string[];
    removals: ProfileReviewRemoval[];
    /** Sector slugs in the order the API sent them, before the alphabetical display sort: the 360 picks a parent in that order. */
    sectorOrder?: string[];
};

export function buildProfileView({ workfieldSlugs, primarySlug, catalog, highlights, removals, sectorOrder = [] }: ProfileViewInput): ProfileView {
    const owned = new Set(workfieldSlugs);
    const pending = new Set(removals.map((removal) => removal.slug));
    const sectors = catalog.filter((sector) => owned.has(sector.slug) || sector.children.some((child) => owned.has(child.slug)));
    const mainSector = sectors.find((sector) => sector.slug === primarySlug) ?? sectors[0] ?? null;

    // Same rule as the 360 Sectors tab: a shared specialty sits on the primary sector, else on the first sector the pro holds, else on its first sector.
    const rank = (slug: string): number => (sectorOrder.includes(slug) ? sectorOrder.indexOf(slug) : Number.MAX_SAFE_INTEGER);
    const ownerOf = (slug: string): string | undefined => {
        const parents = sectors
            .filter((sector) => sector.children.some((child) => child.slug === slug))
            .map((sector) => sector.slug)
            .sort((a, b) => rank(a) - rank(b));
        if (mainSector && parents.includes(mainSector.slug)) return mainSector.slug;
        return parents.find((parent) => owned.has(parent)) ?? parents[0];
    };
    const shows = (sector: CatalogSector, slug: string): boolean => sector === mainSector || owned.has(sector.slug) || ownerOf(slug) === sector.slug;

    const views = sectors.map<ProfileSectorView>((sector) => ({
        slug: sector.slug,
        name: sector.name,
        chips: sector.children
            .filter((child) => owned.has(child.slug) && shows(sector, child.slug))
            .map((child) => ({
                slug: child.slug,
                name: child.name,
                favourite: highlights.includes(child.slug),
                pendingRemoval: pending.has(child.slug),
                sharedWith: sectors.filter((other) => other.children.some((sibling) => sibling.slug === child.slug)).map((other) => other.name),
            })),
    }));

    const main = views.find((view) => view.slug === mainSector?.slug) ?? null;
    const favourites = (view: ProfileSectorView): number => view.chips.filter((chip) => chip.favourite).length;
    // A held sector with none of its specialties is an empty card with nothing to star (TDD-7042): only a sector the catalogue offers no specialty for keeps its card.
    const standalone = new Set(sectors.filter((sector) => sector.children.length === 0).map((sector) => sector.slug));
    const others = views.filter((view) => view !== main && (view.chips.length > 0 || standalone.has(view.slug))).sort((a, b) => favourites(b) - favourites(a) || b.chips.length - a.chips.length);
    return { main, others };
}

export function orderFavourites(view: ProfileView, highlights: string[]): string[] {
    const onPage = [view.main, ...view.others].flatMap((sector) => sector?.chips.map((chip) => chip.slug) ?? []);
    const ordered = [...new Set(onPage)].filter((slug) => highlights.includes(slug));
    return [...ordered, ...highlights.filter((slug) => !ordered.includes(slug))];
}

/** Cards stay where they were while the pro clicks stars: this key ignores the stars, so the order is computed again only when a card or a specialty comes or goes. */
export function othersLayoutKey(others: ProfileSectorView[]): string {
    return others
        .map(
            (sector) =>
                `${sector.slug}:${sector.chips
                    .map((chip) => chip.slug)
                    .sort()
                    .join(',')}`
        )
        .sort()
        .join(';');
}

export function keepOrder(others: ProfileSectorView[], order: string[]): ProfileSectorView[] {
    const rank = (slug: string): number => (order.includes(slug) ? order.indexOf(slug) : order.length);
    return [...others].sort((a, b) => rank(a.slug) - rank(b.slug));
}

export type HighlightToggle = { slug: string; on: boolean };

/** Stars clicked but not yet confirmed, replayed in click order on top of the last answer of the API, never beyond the limit. */
export function replayHighlights(highlights: string[], toggles: HighlightToggle[]): string[] {
    return toggles.reduce((current, { slug, on }) => {
        if (!on) return current.filter((highlight) => highlight !== slug);
        return current.includes(slug) || current.length >= MAX_HIGHLIGHTS ? current : [...current, slug];
    }, highlights);
}

export function countDecisionCards(cards: FlowCard[]): number {
    return cards.filter(isDecisionCard).length;
}

export type FlowRecap = { removed: number; kept: number; added: number; declined: number; languagesConfirmed: boolean; primary: 'changed' | 'kept' | null };

export function buildRecap(decisions: Record<string, ProfileReviewDecisionType>): FlowRecap {
    const values = Object.values(decisions);
    const count = (type: ProfileReviewDecisionType): number => values.filter((value) => value === type).length;
    return {
        removed: count(PROFILE_REVIEW_DECISION.REMOVE),
        kept: count(PROFILE_REVIEW_DECISION.KEEP),
        added: count(PROFILE_REVIEW_DECISION.ADD),
        declined: count(PROFILE_REVIEW_DECISION.DECLINE_ADD),
        languagesConfirmed: count(PROFILE_REVIEW_DECISION.CONFIRM_LANGUAGES) > 0,
        primary: count(PROFILE_REVIEW_DECISION.CHANGE_PRIMARY) > 0 ? 'changed' : count(PROFILE_REVIEW_DECISION.KEEP_PRIMARY) > 0 ? 'kept' : null,
    };
}

export function normalizeProfileReview(raw: Record<string, unknown>, professionalId: string): ProfileReview {
    const list = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);
    const languages = (raw.languages ?? {}) as { items?: unknown; confirmedAt?: unknown };
    const primary = (raw.primary ?? {}) as { current?: unknown; suggested?: unknown; gap?: unknown };
    const text = (value: unknown): string | null => (typeof value === 'string' && value ? value : null);

    return {
        professionalId: text(raw.professionalId) ?? professionalId,
        isPremium: raw.isPremium === true,
        band: Object.values(PROFILE_REVIEW_BAND).includes(raw.band as ProfileReviewBand) ? (raw.band as ProfileReviewBand) : null,
        languages: { items: list<string>(languages.items), confirmedAt: text(languages.confirmedAt) },
        reviewedAt: text(raw.reviewedAt),
        removals: list<ProfileReviewRemoval>(raw.removals),
        additions: list<ProfileReviewAddition>(raw.additions).map((addition) => ({ ...addition, because: list<string>(addition.because) })),
        primary: { current: text(primary.current), suggested: text(primary.suggested), gap: typeof primary.gap === 'number' ? primary.gap : null },
        highlights: list<string>(raw.highlights),
    };
}
